<div class="dashboard opensis-dashboard">
  <!-- Context bar (School / Year / Term) -->
  <section class="context-bar" *ngIf="!loading && !loadError">
    <mat-icon class="ctx-icon" aria-hidden="true">filter_alt</mat-icon>
    <span class="ctx-label">Context</span>
    <mat-form-field appearance="outline" class="ctx-field">
      <mat-label>School</mat-label>
      <input matInput [value]="schoolHeadline() || 'Your school'" readonly />
    </mat-form-field>
    <mat-form-field appearance="outline" class="ctx-field">
      <mat-label>Academic year</mat-label>
      <mat-select [value]="schoolContext.getAcademicYear()" (selectionChange)="schoolContext.setAcademicYear($event.value); onContextChange()">
        <mat-option *ngFor="let y of schoolContext.academicYears" [value]="y">{{ y }}</mat-option>
      </mat-select>
    </mat-form-field>
    <mat-form-field appearance="outline" class="ctx-field">
      <mat-label>Term</mat-label>
      <mat-select [value]="schoolContext.getTermId()" (selectionChange)="schoolContext.setTermId($event.value); onContextChange()">
        <mat-option *ngFor="let t of schoolContext.terms" [value]="t.id">{{ t.label }}</mat-option>
      </mat-select>
    </mat-form-field>
  </section>

  <header class="dashboard-header">
    <div>
      <h1 class="dashboard-title">Dashboard</h1>
      <p class="greeting">
        Hello, <span class="greeting-name">{{ greetingName() }}</span>
        <span *ngIf="roleLabel()" class="role-pill">{{ roleLabel() }}</span>
      </p>
      <p class="today-line">{{ today | date : 'EEEE, MMMM d, y' }}</p>
    </div>
  </header>

  <div *ngIf="loading" class="state-block">
    <mat-spinner diameter="44"></mat-spinner>
    <span>Loading overview…</span>
  </div>

  <div *ngIf="!loading && loadError" class="state-block error">
    <mat-icon color="warn">error_outline</mat-icon>
    <p>{{ loadError }}</p>
    <button mat-stroked-button type="button" color="primary" (click)="retry()">Retry</button>
  </div>

  <ng-container *ngIf="!loading && !loadError && data">
    <!-- KPI strip -->
    <div class="metrics kpi-strip">
      <mat-card class="metric-card metric-students">
        <mat-card-content>
          <div class="metric-icon-wrap" aria-hidden="true"><mat-icon>groups</mat-icon></div>
          <div class="metric-body">
            <span class="metric-label">Students</span>
            <span class="metric-value">{{ data.studentCount != null ? data.studentCount : '—' }}</span>
          </div>
        </mat-card-content>
      </mat-card>
      <mat-card class="metric-card metric-teachers">
        <mat-card-content>
          <div class="metric-icon-wrap" aria-hidden="true"><mat-icon>badge</mat-icon></div>
          <div class="metric-body">
            <span class="metric-label">Staff</span>
            <span class="metric-value">{{ data.teacherCount != null ? data.teacherCount : '—' }}</span>
          </div>
        </mat-card-content>
      </mat-card>
      <mat-card class="metric-card metric-parents">
        <mat-card-content>
          <div class="metric-icon-wrap" aria-hidden="true"><mat-icon>family_restroom</mat-icon></div>
          <div class="metric-body">
            <span class="metric-label">Parents</span>
            <span class="metric-value">—</span>
            <span class="metric-hint">API: /dashboard/summary</span>
          </div>
        </mat-card-content>
      </mat-card>
      <mat-card class="metric-card metric-alerts">
        <mat-card-content>
          <div class="metric-icon-wrap" aria-hidden="true"><mat-icon>notifications_active</mat-icon></div>
          <div class="metric-body">
            <span class="metric-label">Notifications</span>
            <span class="metric-value">{{ data.unreadNotifications != null ? data.unreadNotifications : '—' }}</span>
          </div>
        </mat-card-content>
      </mat-card>
    </div>

    <div class="main-layout">
      <div class="main-col">
        <!-- Attendance this week -->
        <mat-card class="widget-card attendance-widget">
          <mat-card-title class="widget-title">
            <span>Attendance this week</span>
            <span class="widget-nav">
              <button mat-icon-button type="button" (click)="shiftWeek(-1)" aria-label="Previous week"><mat-icon>chevron_left</mat-icon></button>
              <button mat-icon-button type="button" (click)="shiftWeek(1)" aria-label="Next week"><mat-icon>chevron_right</mat-icon></button>
            </span>
          </mat-card-title>
          <mat-card-subtitle>{{ weekTitle() }}</mat-card-subtitle>
          <mat-card-content>
            <div *ngIf="weekLoading" class="week-loading"><mat-spinner diameter="32"></mat-spinner></div>
            <div class="attn-kpi" *ngIf="attendanceWeek && !weekLoading">
              <div class="kpi-present">
                {{ attendanceWeek.presentPercent | number : '1.2-2' }}% <span class="kpi-sub">Present this week</span>
              </div>
              <div class="kpi-warn" *ngIf="attendanceWeek.missingAttendanceCount > 0">
                <mat-icon>warning</mat-icon>
                {{ attendanceWeek.missingAttendanceCount }} missing attendance(s)
              </div>
            </div>
            <p class="api-hint" *ngIf="!attendanceWeek">Connect <code>GET /api/v1/dashboard/attendance-week</code> for weekly chart data.</p>
            <div class="week-chart" *ngIf="attendanceWeek?.days?.length && !weekLoading">
              <div class="day-col" *ngFor="let day of attendanceWeek.days">
                <div class="stack" [title]="day.date">
                  <div class="seg present" [style.height.%]="stackPct(day, 'present')"></div>
                  <div class="seg absent" [style.height.%]="stackPct(day, 'absent')"></div>
                  <div class="seg half" [style.height.%]="stackPct(day, 'halfDay')"></div>
                </div>
                <span class="dlabel">{{ day.date | date : 'EEE' }}</span>
              </div>
            </div>
            <div class="bar-legend">
              <span><i class="dot present"></i> Present</span>
              <span><i class="dot absent"></i> Absent</span>
              <span><i class="dot half"></i> Half day</span>
            </div>
          </mat-card-content>
        </mat-card>

        <!-- Demographics row -->
        <div class="demo-row">
          <mat-card class="widget-card pie-card">
            <mat-card-title>Students by gender</mat-card-title>
            <mat-card-content>
              <div class="pie-wrap" *ngIf="demographicsGender?.slices?.length">
                <div class="pie-ring" [style.background]="pieGradient(demographicsGender?.slices)"></div>
                <ul class="pie-legend">
                  <li *ngFor="let s of demographicsGender.slices; let i = index">
                    <i class="key" [style.background]="s.color || piePalette[i % piePalette.length]"></i>
                    {{ s.label }} ({{ s.count }})
                  </li>
                </ul>
              </div>
              <p class="empty-hint" *ngIf="!demographicsGender?.slices?.length">No data — implement GET /api/v1/dashboard/students/demographics?dimension=GENDER</p>
            </mat-card-content>
          </mat-card>
          <mat-card class="widget-card pie-card">
            <mat-card-title>Students by ethnicity</mat-card-title>
            <mat-card-content>
              <div class="pie-wrap" *ngIf="demographicsEthnicity?.slices?.length">
                <div class="pie-ring" [style.background]="pieGradient(demographicsEthnicity?.slices)"></div>
                <ul class="pie-legend">
                  <li *ngFor="let s of demographicsEthnicity.slices; let i = index">
                    <i class="key" [style.background]="s.color || piePalette[i % piePalette.length]"></i>
                    {{ s.label }} ({{ s.count }})
                  </li>
                </ul>
              </div>
              <p class="empty-hint" *ngIf="!demographicsEthnicity?.slices?.length">No data — implement GET …/demographics?dimension=ETHNICITY</p>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- At-risk tables -->
        <div class="tables-row">
          <mat-card class="widget-card">
            <mat-card-title>Students with &gt;10% absence</mat-card-title>
            <mat-card-content>
              <table class="simple-table" *ngIf="highAbsenceRows?.length">
                <thead><tr><th>ID</th><th>Name</th><th>Absences</th></tr></thead>
                <tbody>
                  <tr *ngFor="let r of highAbsenceRows">
                    <td>{{ r.studentId }}</td>
                    <td>{{ r.fullName }}</td>
                    <td>{{ r.metricValue }}</td>
                  </tr>
                </tbody>
              </table>
              <p class="empty-hint" *ngIf="!highAbsenceRows?.length">No rows — GET /api/v1/dashboard/students/high-absence</p>
            </mat-card-content>
          </mat-card>
          <mat-card class="widget-card">
            <mat-card-title>Students with lowest GPA</mat-card-title>
            <mat-card-content>
              <table class="simple-table" *ngIf="lowestGpaRows?.length">
                <thead><tr><th>ID</th><th>Name</th><th>CGPA</th></tr></thead>
                <tbody>
                  <tr *ngFor="let r of lowestGpaRows">
                    <td>{{ r.studentId }}</td>
                    <td>{{ r.fullName }}</td>
                    <td>{{ r.metricValue }}</td>
                  </tr>
                </tbody>
              </table>
              <p class="empty-hint" *ngIf="!lowestGpaRows?.length">No rows — GET /api/v1/dashboard/students/lowest-gpa</p>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Billing insight -->
        <mat-card class="widget-card billing-widget">
          <mat-card-title>Billing insight</mat-card-title>
          <mat-card-subtitle>Year to date · {{ billingFrom }} → {{ billingTo }}</mat-card-subtitle>
          <mat-card-content>
            <div class="billing-bars" *ngIf="billingInsight">
              <div class="bill-item">
                <span>Collected</span>
                <div class="bill-track"><div class="bill-fill collected" [style.width.%]="billingBarPct(billingInsight.collectedYtd, billingInsight)"></div></div>
                <span class="bill-amt">{{ billingInsight.collectedYtd | number : '1.2-2' }}</span>
              </div>
              <div class="bill-item">
                <span>Overdue</span>
                <div class="bill-track"><div class="bill-fill overdue" [style.width.%]="billingBarPct(billingInsight.overdue, billingInsight)"></div></div>
                <span class="bill-amt">{{ billingInsight.overdue | number : '1.2-2' }}</span>
              </div>
              <div class="bill-item">
                <span>To be collected</span>
                <div class="bill-track"><div class="bill-fill pending" [style.width.%]="billingBarPct(billingInsight.toBeCollected, billingInsight)"></div></div>
                <span class="bill-amt">{{ billingInsight.toBeCollected | number : '1.2-2' }}</span>
              </div>
            </div>
            <div class="fee-type-list" *ngIf="billingInsight?.byFeeType?.length">
              <h4 class="subhead">By fee type</h4>
              <ul>
                <li *ngFor="let f of billingInsight.byFeeType">
                  <span>{{ f.label }}</span>
                  <strong>{{ f.amount | number : '1.2-2' }}</strong>
                </li>
              </ul>
            </div>
            <p class="empty-hint" *ngIf="!billingInsight">Connect GET /api/v1/dashboard/billing-insight</p>
          </mat-card-content>
        </mat-card>

        <!-- Today tile + quick actions -->
        <div class="bottom-tools">
          <mat-card class="widget-card today-mini">
            <mat-card-title>Today’s attendance</mat-card-title>
            <mat-card-content>
              <div class="big-val">{{ attendanceHeadline() }}</div>
              <p class="sub">{{ attendanceSubtext() }}</p>
            </mat-card-content>
          </mat-card>
          <mat-card class="widget-card shortcuts-panel">
            <mat-card-title>Quick actions</mat-card-title>
            <mat-card-content>
              <div class="shortcut-grid">
                <button mat-stroked-button type="button" color="primary" (click)="go('/home/students')"><mat-icon>person_search</mat-icon> Students</button>
                <button *ngIf="isAdmin()" mat-stroked-button type="button" color="primary" (click)="go('/home/teachers')"><mat-icon>supervisor_account</mat-icon> Teachers</button>
                <button mat-stroked-button type="button" color="primary" (click)="go('/home/attendance')"><mat-icon>how_to_reg</mat-icon> Attendance</button>
                <button *ngIf="isAdmin() || isTeacher()" mat-stroked-button type="button" color="primary" (click)="go('/home/grades')"><mat-icon>grading</mat-icon> Grades</button>
                <button mat-stroked-button type="button" (click)="go('/home/admissions')"><mat-icon>assignment_ind</mat-icon> Admissions</button>
                <button *ngIf="isAdmin()" mat-stroked-button type="button" (click)="go('/home/accounts')"><mat-icon>payments</mat-icon> Accounts</button>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </div>

      <!-- Right column -->
      <aside class="side-col">
        <mat-card class="widget-card">
          <mat-card-title>Messages</mat-card-title>
          <mat-card-content>
            <ul class="msg-list" *ngIf="dashboardMessages.length">
              <li *ngFor="let m of dashboardMessages">
                <div class="msg-from">{{ m.fromName }}</div>
                <div class="msg-subj">{{ m.subject }}</div>
                <div class="msg-time">{{ formatMsgDate(m.sentAt) }}</div>
              </li>
            </ul>
            <p class="empty-hint" *ngIf="!dashboardMessages.length">No messages — GET /api/v1/dashboard/messages</p>
          </mat-card-content>
        </mat-card>

        <mat-card class="widget-card calendar-panel">
          <mat-card-title>School calendar</mat-card-title>
          <mat-card-content>
            <mat-calendar [(selected)]="calendarSelected" [dateClass]="calendarDateClass"></mat-calendar>
            <div class="calendar-legend" aria-label="Calendar legend">
              <div class="legend-row"><span class="swatch cal-exam"></span> Exam / test</div>
              <div class="legend-row"><span class="swatch cal-public-holiday"></span> Public holiday</div>
              <div class="legend-row"><span class="swatch cal-second-saturday"></span> Second Saturday</div>
              <div class="legend-row"><span class="swatch cal-sunday"></span> Sunday</div>
            </div>
          </mat-card-content>
        </mat-card>

        <mat-card class="widget-card" *ngIf="upcomingEvents.length">
          <mat-card-title>Events</mat-card-title>
          <mat-card-content>
            <div class="event-item" *ngFor="let e of upcomingEvents">
              <strong>{{ e.title }}</strong>
              <div class="event-dates">{{ e.startDate }}<span *ngIf="e.endDate && e.endDate !== e.startDate"> – {{ e.endDate }}</span></div>
            </div>
          </mat-card-content>
        </mat-card>

        <mat-card class="widget-card behavior-widget">
          <mat-card-title>
            Open behavior cases
            <span class="badge-count" *ngIf="behaviorCases?.totalOpen != null">({{ behaviorCases.totalOpen }})</span>
          </mat-card-title>
          <mat-card-content>
            <table class="simple-table tight" *ngIf="behaviorCases?.items?.length">
              <thead><tr><th>Ref</th><th>Student</th><th>Date</th><th>Status</th></tr></thead>
              <tbody>
                <tr *ngFor="let b of (behaviorCases?.items || [])">
                  <td>{{ b.referralCode || b.id }}</td>
                  <td>{{ b.studentSummary || '—' }}</td>
                  <td>{{ b.incidentDate | date : 'mediumDate' }}</td>
                  <td><span class="status-pill">{{ b.status }}</span></td>
                </tr>
              </tbody>
            </table>
            <p class="empty-hint" *ngIf="!behaviorCases?.items?.length">No open cases — GET /api/v1/dashboard/behavior-cases</p>
          </mat-card-content>
        </mat-card>
      </aside>
    </div>
  </ng-container>
</div>
